import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Search, ShoppingCart, User, Phone, Mail, Package } from "lucide-react";
import { API } from "@/lib/api";
import { productImgUrl } from "@/lib/imageUtils";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { MegaMenu } from "@/components/MegaMenu";
import SHOP from "@/config/shop";

export default function Header() {
  const { itemCount } = useCart();
  const { user, logout } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState(null);
  const [logoUrl, setLogoUrl] = useState('/images/logo.png');
  const [contactPhone, setContactPhone] = useState(SHOP.contact.defaultPhone);
  const [contactEmail, setContactEmail] = useState(SHOP.contact.defaultEmail);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/settings`).then(res => {
      if (res.data.logo_url) setLogoUrl(res.data.logo_url);
      if (res.data.contact_phone) setContactPhone(res.data.contact_phone);
      if (res.data.contact_email) setContactEmail(res.data.contact_email);
    }).catch(() => {});
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/produkty?search=${encodeURIComponent(searchQuery)}`);
      setSuggestions(null);
    }
  };

  useEffect(() => {
    if (searchQuery.length >= 2) {
      const timer = setTimeout(async () => {
        try {
          const res = await axios.get(`${API}/search/suggest?q=${encodeURIComponent(searchQuery)}`);
          setSuggestions(res.data);
        } catch (e) {
          console.error(e);
        }
      }, 300);
      return () => clearTimeout(timer);
    } else {
      setSuggestions(null);
    }
  }, [searchQuery]);

  return (
    <header className="sticky top-0 z-50 bg-white border-b border-slate-200 shadow-sm">
      <div className="bg-[#004E98] text-white text-xs py-1.5">
        <div className="max-w-7xl mx-auto px-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {contactPhone}</span>
            <span className="hidden sm:flex items-center gap-1"><Mail className="w-3 h-3" /> {contactEmail}</span>
          </div>
          <div className="hidden md:flex items-center gap-4">
            <Link to="/o-nas" className="hover:underline">O nás</Link>
            <Link to="/dokumenty" className="hover:underline">Dokumenty</Link>
            <Link to="/doprava" className="hover:underline">Doprava</Link>
            <Link to="/kontakty" className="hover:underline">Kontakty</Link>
          </div>
          <div className="md:hidden">Doprava zdarma od 2000 Kč</div>
        </div>
      </div>
      
      <div className="max-w-7xl mx-auto px-4 py-2 sm:py-3">
        <div className="flex items-center justify-between gap-2 sm:gap-4">
          <Link to="/" className="flex-shrink-0" data-testid="logo-link">
            <img src={logoUrl} alt={SHOP.name} className="h-8 sm:h-10 lg:h-12 w-auto object-contain" />
          </Link>

          <div className="flex-1 max-w-2xl relative hidden sm:block">
            <form onSubmit={handleSearch} className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Hledat produkty..."
                className="w-full h-10 lg:h-11 pl-4 pr-12 border border-slate-300 rounded-sm focus:border-[#004E98] focus:ring-1 focus:ring-[#004E98] outline-none text-sm"
                data-testid="search-input"
              />
              <button type="submit" className="absolute right-1 top-1 h-8 lg:h-9 w-10 bg-[#004E98] text-white rounded-sm flex items-center justify-center hover:bg-[#003B73] transition-colors" data-testid="search-button">
                <Search className="w-5 h-5" />
              </button>
            </form>
            
            {suggestions && suggestions.length > 0 && (
              <div className="absolute top-full left-0 right-0 bg-white border border-slate-200 rounded-b shadow-lg mt-0 z-50 max-h-[400px] overflow-y-auto" data-testid="search-suggestions">
                <div className="p-2">
                  <div className="text-[10px] text-slate-400 uppercase font-bold px-2 mb-1">Produkty</div>
                  {suggestions.map(prod => (
                    <Link 
                      key={prod.id_product} 
                      to={`/produkt/${prod.id_product}`} 
                      className="flex items-center gap-3 py-2 px-2 hover:bg-slate-50 rounded transition-colors" 
                      onClick={() => { setSuggestions(null); setSearchQuery(''); }}
                    >
                      <div className="w-10 h-10 bg-slate-100 rounded flex-shrink-0 overflow-hidden flex items-center justify-center">
                        {productImgUrl(prod, 'small') ? (
                          <img src={productImgUrl(prod, 'small')} alt="" className="w-full h-full object-contain" />
                        ) : (
                          <Package className="w-5 h-5 text-slate-300" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-slate-800 truncate">{prod.name}</div>
                        {prod.reference && <div className="text-xs text-slate-400">Kód: {prod.reference}</div>}
                      </div>
                      <div className="text-sm font-bold text-[#004E98] flex-shrink-0">{prod.price_vat?.toFixed(2)} Kč</div>
                    </Link>
                  ))}
                </div>
                <div className="border-t border-slate-100 p-2">
                  <button onClick={handleSearch} className="w-full text-center text-sm text-[#004E98] font-medium py-2 hover:bg-slate-50 rounded transition-colors" data-testid="search-all-results">
                    Zobrazit všechny výsledky pro "{searchQuery}"
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center gap-1 sm:gap-2">
            <button onClick={() => { const el = document.getElementById('mobile-search'); if (el) el.classList.toggle('hidden'); }} className="sm:hidden p-2 text-slate-600" data-testid="mobile-search-toggle">
              <Search className="w-5 h-5" />
            </button>
            {user ? (
              <div className="relative group">
                <button className="flex items-center gap-2 px-3 py-2 text-slate-600 hover:text-[#004E98] transition-colors" data-testid="user-menu-button">
                  <User className="w-5 h-5" />
                  <span className="hidden sm:inline text-sm">{user.firstname}</span>
                </button>
                <div className="absolute right-0 top-full mt-1 bg-white border border-slate-200 rounded-sm shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all w-48 z-50">
                  <Link to="/muj-ucet" className="block px-4 py-2 text-sm hover:bg-slate-50">Můj účet</Link>
                  <Link to="/objednavky" className="block px-4 py-2 text-sm hover:bg-slate-50">Moje objednávky</Link>
                  {user.is_admin && <Link to="/admin" className="block px-4 py-2 text-sm hover:bg-slate-50 text-[#004E98]">Administrace</Link>}
                  <button onClick={logout} className="w-full text-left px-4 py-2 text-sm hover:bg-slate-50 text-red-600" data-testid="logout-button">Odhlásit se</button>
                </div>
              </div>
            ) : (
              <Link to="/prihlaseni" className="flex items-center gap-2 px-3 py-2 text-slate-600 hover:text-[#004E98] transition-colors" data-testid="login-link">
                <User className="w-5 h-5" />
                <span className="hidden sm:inline text-sm">Přihlásit</span>
              </Link>
            )}
            
            <Link to="/kosik" className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-2 bg-[#FF6B35] text-white rounded-sm hover:bg-[#E85D2A] transition-colors relative" data-testid="cart-link">
              <ShoppingCart className="w-5 h-5" />
              <span className="hidden sm:inline text-sm font-medium">Košík</span>
              {itemCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#004E98] text-white text-xs rounded-full flex items-center justify-center">{itemCount}</span>
              )}
            </Link>
          </div>
        </div>
        <div id="mobile-search" className="sm:hidden hidden mt-2">
          <form onSubmit={handleSearch} className="relative">
            <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Hledat produkty..." className="w-full h-10 pl-4 pr-12 border border-slate-300 rounded-sm text-sm" />
            <button type="submit" className="absolute right-1 top-1 h-8 w-10 bg-[#004E98] text-white rounded-sm flex items-center justify-center"><Search className="w-5 h-5" /></button>
          </form>
        </div>
      </div>

      <MegaMenu />
    </header>
  );
}
